import mongoose from "mongoose";
import * as uuid from "uuid";

export interface OrderItemAccessory {
  _id: mongoose.Schema.Types.ObjectId;
  sku: string;
  name: string;
  category: string;
  description: string;
  imageUrl: string;
  bigDealPrice: string;
  bigDealPriceOrdered?: string;
}

export interface OrderItemDisplay {
  _id: mongoose.Schema.Types.ObjectId;
  displayId: number;
  sku: string;
  name: string;
  category: string;
  nativeResolution: string;
  contractRatio: string; // sic
  signalInputConnectors: string;
  imageUrl?: string;
  bigDealPrice: number;
  bigDealPriceOrdered?: number;
}

export interface OrderItemService {
  _id: mongoose.Schema.Types.ObjectId;
  parentId: string | number;
  parentType: string;
  sku: string;
  serviceOffer: string;
  description1?: string;
  description2?: string;
  description3?: string;
  description4?: string;
}

export interface OrderItemContent {
  _id: mongoose.Schema.Types.ObjectId;
  bundleId: string | number;
  bundleName: string;
  bundleCurrency: string;
  bundleCurrencyOrdered?: string;
  bundlePricePerMonth: number;
  bundlePricePerMonthOrdered?: number;
  bundleTermLength: number;
  mainHardwareSKU: string;
  mainHardwareCategory: string;
  mainHardwareImageUrl?: string;
  skuLocalizationCode?: string;
  configId: string;
  bundleBigDealPrice: number;
  bundleBigDealPriceOrdered?: number;
  accessories: OrderItemAccessory[];
  displays: OrderItemDisplay[];
  services: OrderItemService[];
}

export interface OrderItem {
  _id: mongoose.Schema.Types.ObjectId;
  item: OrderItemContent;
  quantity: number;
}

export interface OrderStatusUser {
  firstName: string;
  lastName: string;
  email: string;
  id: string;
}

export interface OrderStatusUserRoleInfo {
  name: string;
  description: string;
  roleType: string;
}

export interface OrderStatus {
  rejectionReason: string;
  cancellationReason: string;
  status: string;
  statusDateTime: string;
  /** @deprecated */
  estimatedDeliveryDate?: string;
  user?: OrderStatusUser;
  userRoleInfo?: OrderStatusUserRoleInfo;
}

export interface OrderAdditionalInfo {
  bigDealNumber?: string;
  hpfsNumber?: string;
}

export interface OrderShipTo {
  firstName: string;
  lastName: string;
  primaryPhone: string;
  email: string;
  /** @deprecated */
  additionalEmail?: string;
  /** @deprecated */
  getEmail?: boolean;
  /** @deprecated */
  getAdditionalEmail?: string;
  deliveryInstructions?: string;
  poNumber?: string;
  orderReference?: string;
  priorityRequest?: string;
  shipComplete?: string;
  onSiteDate?: string;
  customerOrderNumber?: string;
  portalCustomerOrderNumber?: string;
}

export interface OrderSite {
  name1?: string;
  name2?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateOrProvince?: string;
  postalCode?: string;
  country?: string;
  cbnCrs?: string; // SCID
  billTo?: string;
  siteName?: string;
  state?: string;
  zip?: string;
}

export interface OrderHomeAddress {
  homeBuilding?: string;
  homeCity?: string;
  homeState?: string;
  homeZip?: string;
  homeCountry?: string;
}

export interface OrderShipmentGroupSerialNumber {
  _id: mongoose.Schema.Types.ObjectId;
  serialNumber?: string;
}

export interface OrderShipmentGroupProduct {
  _id: mongoose.Schema.Types.ObjectId;
  quantity?: number;
  correlationId?: string;
  lineItemNumber?: string;
  productDescription?: string;
  productNumber?: string;
  serialNumbers?: string[];
  serialNumberList?: OrderShipmentGroupSerialNumber[];
}

export interface OrderShipmentGroup {
  groupId?: string;
  status?: string;
  trackingNumber?: string;
  trackAndTraceLink?: string;
  plannedShipDate?: string;
  shipDate?: string;
  plannedDeliveryDate?: string;
  deliveryDate?: string;
  products?: OrderShipmentGroupProduct[];
}

export interface OrderITSMItem {
  _id: mongoose.Schema.Types.ObjectId;
  correlationId?: string;
  solutionId?: string;
  localizationCode?: string;
  quantity?: number;
}

export interface OrderContact {
  email: string;
  notify: boolean;
}

export interface OrderAsset {
  productNumber?: string;
  serialNumber?: string;
}

export interface Order {
  _id: mongoose.Schema.Types.ObjectId;
  id: string;
  version: number;
  transactionId?: string;
  contractId: string;
  customerContractId: string;
  isITSMOrder?: boolean;
  /** @deprecated */
  preOrderId?: string;
  /** @deprecated */
  purchaseOrderFileName?: string;
  customerId: string;
  _text: string;
  items?: OrderItem[];
  orderNumber?: string;
  hpOrderNumber?: string;
  orderStatus: OrderStatus;
  orderStatusHistory?: OrderStatus[];
  additionalInfo?: OrderAdditionalInfo;
  shipTo?: OrderShipTo;
  site?: OrderSite;
  homeAddress?: OrderHomeAddress;
  shipmentGroups?: OrderShipmentGroup[];
  itsmOrderItems?: OrderITSMItem[];
  siteText?: string;
  shipToText?: string;
  singleOrderNumber: string;
  quantity?: string;
  submittedDate?: string;
  orderOriginationDate?: string;
  estimatedDeliveryDate?: string;
  deliveryDate?: string;
  estimatedShipDate?: string;
  shipDate?: string;
  status?: string;
  contacts: OrderContact[];
  assets?: OrderAsset[];
}

export const OrderSchema = new mongoose.Schema<Order>({
  id: { type: String, index: true, default: () => uuid.v1(), unique: true },
  version: { type: Number },
  transactionId: { type: String, index: true },
  contractId: { type: String, index: true },
  customerContractId: { type: String, index: true },
  isITSMOrder: Boolean,
  preOrderId: String,
  purchaseOrderFileName: String,
  customerId: { type: String, index: true },
  _text: String,
  items: [
    {
      item: {
        bundleId: mongoose.Schema.Types.Mixed,
        bundleName: String,
        bundleCurrency: String,
        bundleCurrencyOrdered: String,
        bundlePricePerMonth: Number,
        bundlePricePerMonthOrdered: mongoose.Schema.Types.Number,
        bundleTermLength: mongoose.Schema.Types.Number,
        mainHardwareSKU: String,
        mainHardwareCategory: String,
        mainHardwareImageUrl: String,
        skuLocalizationCode: String,
        configId: String,
        bundleBigDealPrice: mongoose.Schema.Types.Number,
        bundleBigDealPriceOrdered: mongoose.Schema.Types.Number,
        accessories: [
          {
            sku: String,
            name: String,
            category: String,
            description: String,
            imageUrl: String,
            bigDealPrice: String,
            bigDealPriceOrdered: String,
          },
        ],
        displays: [
          {
            displayId: mongoose.Schema.Types.Number,
            sku: String,
            name: String,
            category: String,
            nativeResolution: String,
            contractRatio: String,
            signalInputConnectors: String,
            imageUrl: String,
            bigDealPrice: mongoose.Schema.Types.Number,
            bigDealPriceOrdered: mongoose.Schema.Types.Number,
          },
        ],
        services: [
          {
            parentId: mongoose.Schema.Types.Mixed,
            parentType: String,
            sku: String,
            serviceOffer: String,
            description1: String,
            description2: String,
            description3: String,
            description4: String,
          },
        ],
      },
      quantity: mongoose.Schema.Types.Number,
    },
  ],
  orderNumber: { type: String, index: true },
  hpOrderNumber: { type: String, index: true },
  orderStatus: {
    rejectionReason: String,
    cancellationReason: String,
    status: String,
    statusDateTime: String,
    estimatedDeliveryDate: String,
    user: {
      firstName: String,
      lastName: String,
      email: String,
      id: String,
    },
    userRoleInfo: {
      name: String,
      description: String,
      roleType: String,
    },
  },
  orderStatusHistory: [
    {
      rejectionReason: String,
      status: String,
      statusDateTime: String,
      estimatedDeliveryDate: String,
      user: {
        firstName: String,
        lastName: String,
        email: String,
        id: String,
      },
      userRoleInfo: {
        name: String,
        description: String,
        roleType: String,
      },
    },
  ],
  additionalInfo: {
    bigDealNumber: String,
    hpfsNumber: String,
  },
  shipTo: {
    firstName: String,
    lastName: String,
    primaryPhone: String,
    email: String,
    /** @deprecated */
    additionalEmail: String,
    /** @deprecated */
    getEmail: Boolean,
    /** @deprecated */
    getAdditionalEmail: Boolean,
    deliveryInstructions: String,
    poNumber: String,
    orderReference: String,
    priorityRequest: String,
    shipComplete: String,
    onSiteDate: String,
    customerOrderNumber: String,
    portalCustomerOrderNumber: String,
  },
  site: {
    name1: String,
    name2: String,
    addressLine1: String,
    addressLine2: String,
    city: String,
    stateOrProvince: String,
    postalCode: String,
    country: String,
    cbnCrs: String,
    billTo: String,
    siteName: String,
    state: String,
    zip: String,
  },
  homeAddress: {
    homeBuilding: String,
    homeCity: String,
    homeState: String,
    homeZip: String,
    homeCountry: String,
  },
  shipmentGroups: [
    {
      groupId: String,
      status: String,
      trackingNumber: String,
      trackAndTraceLink: String,
      plannedShipDate: String,
      shipDate: String,
      plannedDeliveryDate: String,
      deliveryDate: String,
      products: [
        {
          quantity: Number,
          correlationId: String,
          lineItemNumber: String,
          productDescription: String,
          productNumber: String,
          serialNumbers: [String],
          serialNumberList: [
            {
              // ITSM may not have implemented
              serialNumber: String,
              // deviceId: String, // note: ITSM didn't implement
            },
          ],
        },
      ],
    },
  ],
  itsmOrderItems: [{
    correlationId: String,
    solutionId: String,
    localizationCode: String,
    quantity: Number,
  }],
  siteText: { type: String, index: true },
  shipToText: { type: String, index: true },
  singleOrderNumber: { type: String, index: true },
  quantity: String,
  submittedDate: String, // submitted (PS Portal orders only)
  orderOriginationDate: String, // accepted (OSS order submission date)
  estimatedDeliveryDate: String, // last SG estimated delivery
  deliveryDate: String, // last SG delivery
  estimatedShipDate: String, // first SG estimated ship
  shipDate: String, // first SG ship
  status: { type: String, index: true },
  contacts: [{
    _id: false,
    email: { type: String, index: true },
    notify: Boolean,
  }],
  assets: [{
    _id: false,
    productNumber: String,
    serialNumber: String,
  }],
});

OrderSchema.index({
  "contacts.email": 1,
  "orderNumber": 1,
});

OrderSchema.index({
  "hpOrderNumber": 1,
  "orderStatusHistory.0.status": 1,
  "orderStatusHistory.0.user.email": 1,
});

OrderSchema.index({
  "orderStatusHistory.0.status": 1,
  "orderStatusHistory.0.user.email": 1,
  "shipTo.portalCustomerOrderNumber": 1,
});

OrderSchema.index({
  "contacts.email": 1,
  "shipTo.customerOrderNumber": 1,
});

OrderSchema.index({
  "assets.productNumber": 1,
  "assets.serialNumber": 1,
});

OrderSchema.index({
  "customerId": 1,
  "submittedDate": -1,
});

OrderSchema.index({
  "customerId": 1,
  "shipmentGroups.products.serialNumbers": -1,
});

OrderSchema.index({
  "customerId": 1,
  "shipmentGroups.products.serialNumberList.serialNumber": -1,
});

export const searchProps: (keyof Order)[] = [
  "orderNumber",
  "hpOrderNumber",
  "siteText",
  "shipToText",
  "singleOrderNumber",
  "quantity",
  "submittedDate",
  "estimatedShipDate",
  "shipDate",
  "estimatedDeliveryDate",
  "deliveryDate",
  "status",
  "orderStatus",
];

export const filterProps: (keyof Order)[] = [
  "status",
];

export const orderUtils = {
  searchProps,
  filterProps,
};
